/**
 * Store picker.
 *
 * WHY it redirects when there is exactly one store: that is the overwhelmingly common case for
 * a single-shop seller, and making them click through a one-item list on every visit is pure
 * friction. Agencies with several shops still get the list.
 */
import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { shortPlanLabel } from "../billing";
import { StatusBadge } from "../components/Badges";
import { ConnectStore } from "../components/ConnectStore";
import { EmptyState } from "../components/States";
import { Shell } from "../components/Shell";
import { formatDate } from "../format";
import { useConnections } from "../stores";

export function Stores(): ReactNode {
  const { connections } = useConnections();
  const only = connections.length === 1 ? connections[0] : undefined;
  if (only) return <Navigate to={`/stores/${only.id}`} replace />;

  return (
    <Shell connectionId={null}>
      <h1 className="page-title">Your stores</h1>
      {connections.length === 0 ? (
        <EmptyState
          title="No stores connected yet"
          hint="Connect a Shopify store to start reconciling its payouts. PayoutAudit reads payouts, balance transactions, orders and disputes — it never writes to your shop."
          action={<ConnectStore />}
        />
      ) : (
        <>
          <div className="cards">
            {connections.map((c) => (
              <Link key={c.id} to={`/stores/${c.id}`} className="card store-card">
                <div className="store-card-head">
                  <span className="store-name">{c.display_name}</span>
                  <StatusBadge status={c.status} />
                </div>
                <dl className="kv">
                  <div><dt>Platform</dt><dd>{c.platform}</dd></div>
                  <div><dt>Shop</dt><dd className="mono">{c.external_shop_id}</dd></div>
                  <div><dt>Currency</dt><dd>{c.default_currency ?? "not reported yet"}</dd></div>
                  <div><dt>Connected</dt><dd>{formatDate(c.installed_at)}</dd></div>
                  <div><dt>Plan</dt><dd>{shortPlanLabel(c.subscription)}</dd></div>
                </dl>
              </Link>
            ))}
          </div>
          <section className="card"><ConnectStore /></section>
        </>
      )}
    </Shell>
  );
}
